import { Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, inject } from '@angular/core';
import { MyBrandAndCompetitors, SelectBrandItem } from '../../../services/dashboard.service';
import { GridApi, ClientSideRowModelModule, ColDef, ModuleRegistry, RowSelectionModule, themeQuartz, GridOptions, GridReadyEvent, RowHeightParams } from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import { CommonModule } from '@angular/common';
import { BrandSelectionRendererComponent } from '../brand-selection-renderer/brand-selection-renderer.component';
import { BrandRowData, BrandSelectionRowData } from '../types';

ModuleRegistry.registerModules([RowSelectionModule]);

@Component({
  selector: 'app-brand-selection-table',
  imports: [AgGridAngular, CommonModule],
  templateUrl: './brand-selection-table.component.html',
  styleUrl: './brand-selection-table.component.scss'
})
export class BrandSelectionTableComponent {
  @Input() data: MyBrandAndCompetitors[] = [];
  @Input() selectedBrands: string[] = [];
  @Output() brandSelectionChange = new EventEmitter<string[]>();

  protected rowData?: BrandSelectionRowData[] = [];
  protected gridApi?: GridApi;
  protected colDefs: ColDef[] = [];
  protected modules = [ClientSideRowModelModule];

  private readonly fadeDepth = 48;
  private readonly hostRef: ElementRef<HTMLElement> = inject(ElementRef);
  private scrollViewport?: HTMLElement;

  protected theme = themeQuartz.withParams({
    fontSize: '14px',
    headerFontSize: '14px',
    fontFamily: 'Poppins',
    headerFontFamily: 'Poppins',
    headerFontWeight: 600,
    headerTextColor: '#FFF',
    cellTextColor: '#FFF',
    headerBackgroundColor: 'transparent',
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    wrapperBorderRadius: 0,
    rowBorder: { color: 'rgba(255, 255, 255, 0.10)', width: 1, style: 'dashed' },
    columnBorder: { color: 'rgba(255, 255, 255, 0.10)', width: 1, style: 'dashed' }
  });

  protected gridOptions: GridOptions = {
    domLayout: 'normal',
    suppressCellFocus: true,
    alwaysShowVerticalScroll: true,
    suppressScrollOnNewData: true,
    suppressScrollWhenPopupsAreOpen: true,
    scrollbarWidth: 6,
    suppressMovableColumns: true,
    defaultColDef: {
      sortable: false,
      resizable: false,
      filter: false,
      wrapHeaderText: true,
      autoHeaderHeight: true,
    },
    getRowHeight: this.getRowHeight.bind(this)
  }

  protected onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.scrollViewport = this.hostRef.nativeElement.querySelector<HTMLElement>('.ag-body-viewport') ?? undefined;
    this.updateFade();
  }

  protected updateFade(): void {
    const el = this.scrollViewport;
    if (!el) return;

    const scrollableBelow = el.scrollHeight - el.clientHeight - el.scrollTop;
    const topFade = this.clampToFadeDepth(el.scrollTop);
    const bottomFade = this.clampToFadeDepth(scrollableBelow);

    const mask = topFade || bottomFade
      ? `linear-gradient(to bottom, transparent 0px, #000 ${topFade}px, #000 calc(100% - ${bottomFade}px), transparent 100%)`
      : 'none';

    el.style.setProperty('mask-image', mask);
    el.style.setProperty('-webkit-mask-image', mask);
  }

  private clampToFadeDepth(distance: number): number {
    return Math.round(Math.max(0, Math.min(distance, this.fadeDepth)));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      this.rowData = this.getRowData();
      this.colDefs = this.getColDef();
    }
  }

  private getRowHeight(params: RowHeightParams): number {
    const tileHeight = 32;
    const gap = 66;

    const brands = params.data?.competitors;
    if (!brands?.length) return tileHeight + gap;


    const itemsPerRow = 10;
    const rows = Math.ceil(brands.length / itemsPerRow);
    const rowGap = (rows - 1) * 12;

    return rows * tileHeight + gap + rowGap;
  }

  private getRowData(): BrandSelectionRowData[] {
    return this.data.map(item => {
      const myBrandIsSelected = this.selectedBrands.includes(item.myBrand.key);
      const competitorsData: BrandRowData[] = item.myCompetitors.map(comp => ({
        ...comp,
        isSelected: this.selectedBrands.includes(comp.key),
      }));
      return {
        myBrand: {
          ...item.myBrand,
          isSelected: myBrandIsSelected,
        },
        competitors: competitorsData,
      };
    });
  }

  private getColDef(): ColDef[] {
    const callbacks = {
      onBrandClicked: (brand: BrandRowData) => { this.onBrandClicked(brand) },
      onSelectAllClicked: (brands: BrandRowData[]) => { this.onSelectAllClicked(brands) },
      onDeselectAllClicked: (brands: BrandRowData[]) => { this.onDeselectAllClicked(brands) }
    }
    return [
      {
        headerName: 'My Brand',
        headerClass: 'my-brand-header',
        headerStyle: { borderRadius: '4px', background: 'rgba(255, 255, 255, 0.10)', padding: '8px', width: '116px', height: '32px', marginRight: '12px' },
        field: 'myBrand',
        width: 130,
        cellRenderer: BrandSelectionRendererComponent,
        cellRendererParams: {
          displayButtons: false,
          centered: true,
          ...callbacks
        }
      },
      {
        headerName: 'My Competitors',
        headerClass: 'competitors-header',
        headerStyle: { borderRadius: '4px', background: 'rgba(255, 255, 255, 0.10)', padding: '8px', width: '456px', height: '32px', marginLeft: '12px' },
        field: 'competitors',
        flex: 1,
        minWidth: 400,
        cellRenderer: BrandSelectionRendererComponent,
        cellRendererParams: {
          displayButtons: true,
          ...callbacks
        }
      }
    ]
  }

  protected onBrandClicked(brand: BrandRowData): void {
    const brandId = brand.key;
    const index = this.selectedBrands.indexOf(brandId);
    if (index > -1) {
      this.selectedBrands.splice(index, 1);
    } else {
      this.selectedBrands.push(brandId);
    }
    this.brandSelectionChange.emit(this.selectedBrands);
  }

  protected onSelectAllClicked(brands: BrandRowData[]): void {
    brands.forEach(brand => {
      const brandKey = brand.key;
      if (!this.selectedBrands.includes(brandKey)) {
        this.selectedBrands.push(brandKey);
      }
    });
    this.brandSelectionChange.emit(this.selectedBrands);
  }

  protected onDeselectAllClicked(brands: BrandRowData[]): void {
    brands.forEach(brand => {
      const brandKey = brand.key;
      const index = this.selectedBrands.indexOf(brandKey);
      if (index > -1) {
        this.selectedBrands.splice(index, 1);
      }
    });
    this.brandSelectionChange.emit(this.selectedBrands);
  }

}
